// Minimal wasm loader - replaces emscripten runtime
import { WasmLoad } from "@pdfking/compatibility"
import type { Bytes } from "@pdfking/tfunc"
import {
  GuidToString,
  IsString,
  Keys,
  LockManager,
  StringToUInt8,
  UUID,
} from "@pdfking/tfunc"

import type { Allocated } from "../types/Allocated"
import type {
  PdfiumArguments,
  PdfiumFunctions,
  PdfiumReturns,
} from "../types/Bindings"
import type { PdfiumApiFactory } from "../types/PdfiumApi"
import type { Pointer } from "../types/Pointer"
import type { WasmExports } from "../types/WasmExports"
import wasm from "../wasm/pdfium.esm.wasm"

// Fixed date for deterministic PDF output (2024-01-01 00:00:00 UTC)
const DATE = () => 1704067200000
const EBADF = () => 8
const ENOENT = () => -44
const EBADF_NEG = () => -8
const ZERO = () => 0
const NOOP = () => {}

const fail = (error: Error | string) => {
  throw IsString(error) ? new Error(error) : error
}

const ERRORS: Record<number, string> = {
  1: "unknown error",
  2: "file not found or could not be opened",
  3: "file not in PDF format or corrupted",
  4: "password required or incorrect password",
  5: "unsupported security scheme",
  6: "page not found or content error",
}

const WASM_TYPE_NAMES: Record<string, string> = {
  i: "i32",
  j: "i64",
  f: "f32",
  d: "f64",
  p: "i32",
}

const WASM_TYPE_CODES: Record<string, number> = {
  i: 127,
  p: 127,
  j: 126,
  f: 125,
  d: 124,
}

// Emscripten-compatible function wrapping for callbacks
const sigToWasmTypes = (sig: string) => ({
  parameters: [...sig.slice(1)].map(c => WASM_TYPE_NAMES[c]),
  results: sig[0] === "v" ? [] : [WASM_TYPE_NAMES[sig[0]]],
})

const uleb128Encode = (n: number, target: number[]) => {
  if (n < 128) target.push(n)
  else target.push(n % 128 | 128, n >> 7)
}

const generateFuncType = (sig: string, target: number[]) => {
  const sigParam = sig.slice(1)
  target.push(96)
  uleb128Encode(sigParam.length, target)
  for (const c of sigParam) target.push(WASM_TYPE_CODES[c])
  if (sig[0] === "v") target.push(0)
  else target.push(1, WASM_TYPE_CODES[sig[0]])
}

const convertJsFunctionToWasm = (
  func: (...args: number[]) => number,
  sig: string
): WebAssembly.ExportValue => {
  // Modern browsers support WebAssembly.Function
  const WasmFunction = (WebAssembly as unknown as Record<string, unknown>)
    .Function as new (type: unknown, func: unknown) => WebAssembly.ExportValue
  if (typeof WasmFunction === "function") {
    return new WasmFunction(sigToWasmTypes(sig), func)
  }
  // Fallback: create tiny wasm module that imports and re-exports the function
  const typeSectionBody: number[] = [1]
  generateFuncType(sig, typeSectionBody)
  const bytes: number[] = [0, 97, 115, 109, 1, 0, 0, 0, 1]
  uleb128Encode(typeSectionBody.length, bytes)
  bytes.push(...typeSectionBody)
  bytes.push(2, 7, 1, 1, 101, 1, 102, 0, 0, 7, 5, 1, 1, 102, 0, 0)
  const module = new WebAssembly.Module(new Uint8Array(bytes))
  const instance = new WebAssembly.Instance(module, { e: { f: func } })
  return instance.exports["f"]
}

// Generic invoke for C++ exception handling / setjmp-longjmp
const createInvoke =
  (exports: WasmExports, returnsValue: boolean) =>
  (index: number, ...args: number[]) => {
    const {
      emscripten_stack_get_current,
      _emscripten_stack_restore,
      setThrew,
      __indirect_function_table,
    } = exports
    const sp = emscripten_stack_get_current()
    try {
      const fn = __indirect_function_table.get(index) as
        | ((...a: number[]) => number)
        | null
      if (fn) return fn(...args)
      return 0
    } catch (e: unknown) {
      _emscripten_stack_restore(sp)
      if (typeof e !== "number") throw e
      setThrew(1, 0)
      return returnsValue ? 0 : undefined
    }
  }

const createImportObject = () => {
  const exports = {} as WasmExports
  const invoke = createInvoke(exports, true)
  const invokeVoid = createInvoke(exports, false)
  const imports: WebAssembly.Imports = {
    env: {
      invoke_ii: invoke,
      invoke_iii: invoke,
      invoke_iiii: invoke,
      invoke_iiiii: invoke,
      invoke_viii: invokeVoid,
      _tzset_js: NOOP,
      _localtime_js: NOOP,
      _gmtime_js: NOOP,
      emscripten_date_now: DATE,
      __syscall_fcntl64: ZERO,
      __syscall_ioctl: ZERO,
      __syscall_openat: ENOENT,
      __syscall_stat64: ENOENT,
      __syscall_newfstatat: ENOENT,
      __syscall_lstat64: ENOENT,
      __syscall_unlinkat: ENOENT,
      __syscall_rmdir: ENOENT,
      __syscall_fstat64: EBADF_NEG,
      __syscall_ftruncate64: EBADF_NEG,
      __syscall_getdents64: EBADF_NEG,
      // Memory growth
      emscripten_resize_heap: (requestedSize: number) => {
        const oldSize = exports.memory.buffer.byteLength
        // Treat as unsigned 32-bit integer
        requestedSize = requestedSize >>> 0

        const pagesToGrow = ((requestedSize - oldSize + 65535) / 65536) | 0
        if (pagesToGrow > 0) {
          try {
            exports.memory.grow(pagesToGrow)
            return 1
          } catch {
            return fail(
              `Failed to grow heap from ${oldSize} to ${requestedSize} bytes`
            )
          }
        }
        return 1
      },
      _abort_js: () => fail("abort"),
      _emscripten_throw_longjmp: () => fail("longjmp"),
    },
    wasi_snapshot_preview1: {
      fd_close: EBADF,
      fd_write: EBADF,
      fd_read: EBADF,
      fd_seek: EBADF,
      fd_sync: ZERO,
      environ_get: ZERO,
      environ_sizes_get: (countPtr: number, sizePtr: number) => {
        const heap = new DataView(exports.memory.buffer)
        heap.setInt32(countPtr, 0, true)
        heap.setInt32(sizePtr, 0, true)
        return 0
      },
    },
  }
  return { imports, exports }
}

const loadPdfiumWasm = async (response: Response) => {
  // Compile module once, reuse for clear()
  const module = await WebAssembly.compileStreaming(response)
  const { schedule } = LockManager()

  return async () => {
    const id = GuidToString(UUID())

    const { imports, exports } = createImportObject()

    const init = () =>
      WebAssembly.instantiate(module, imports).then(instance => {
        Object.assign(exports, instance.exports)
        exports.PDFium_Init()
      })

    await init()

    const getHeap = () => new Uint8Array(exports.memory.buffer)

    const getLastError = async (): Promise<Error | undefined> => {
      const code = await call("FPDF_GetLastError")
      return ERRORS[code] ? new Error(ERRORS[code]) : undefined
    }

    const call = async <M extends PdfiumFunctions>(
      method: M,
      ...args: PdfiumArguments[M]
    ): Promise<PdfiumReturns[M]> => {
      const fn = exports[method] as ((...a: number[]) => number) | undefined
      if (!fn) return fail(`Function ${method} not found`)
      return schedule(id, async () => fn(...args)) as Promise<PdfiumReturns[M]>
    }

    const safeCall = async <M extends PdfiumFunctions>(
      method: M,
      ...args: PdfiumArguments[M]
    ): Promise<PdfiumReturns[M]> => {
      const result = await call(method, ...args)
      if (!result) fail((await getLastError()) || `Failed: ${method}`)
      return result
    }

    const malloc = (size: Bytes): Allocated => {
      const pointer = exports.malloc(size) as Pointer
      return { pointer, free: () => exports.free(pointer) }
    }

    const writeString = (str: string): Allocated => {
      const bytes = StringToUInt8(str)
      const { pointer, free } = malloc((bytes.length + 1) as Bytes)
      writeTo(pointer, bytes)
      getHeap()[pointer + bytes.length] = 0
      return { pointer, free }
    }

    const writeBytes = (data: Uint8Array): Allocated => {
      const { pointer, free } = malloc(data.length as Bytes)
      writeTo(pointer, data)
      return { pointer, free }
    }

    const writeTo = (ptr: Pointer, data: Uint8Array) => getHeap().set(data, ptr)

    const readBytes = (ptr: Pointer, size: Bytes) =>
      new Uint8Array(exports.memory.buffer, ptr, size).slice()

    const readFloats = (ptr: Pointer, count: number) =>
      new Float32Array(exports.memory.buffer, ptr, count)

    const setInt32 = (ptr: Pointer, value: number) =>
      new DataView(exports.memory.buffer).setInt32(ptr, value, true)

    const addCallback = (
      fn: (...args: number[]) => number,
      sig: string
    ): Allocated => {
      const wrapped = convertJsFunctionToWasm(fn, sig)
      const table = exports.__indirect_function_table
      const index = table.length
      table.grow(1)
      table.set(index, wrapped)
      const pointer = index as Pointer
      return {
        pointer,
        free: () => exports.__indirect_function_table.set(index, null),
      }
    }

    const getMemorySize = () => exports.memory.buffer.byteLength as Bytes

    const dispose = async () => {
      for (const key of Keys(exports)) {
        delete exports[key]
      }
    }

    return {
      id,
      call,
      safeCall,
      malloc,
      writeString,
      writeBytes,
      writeTo,
      readBytes,
      readFloats,
      setInt32,
      addCallback,
      getMemorySize,
      init,
      dispose,
    }
  }
}

export const CreatePdfiumFactory: PdfiumApiFactory = (path?: string) =>
  WasmLoad(path || wasm).then(loadPdfiumWasm)
